import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Lock as LockIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { apiService } from '../../services/api';
import { ChangePasswordRequest } from '../../types/user';
import PageLayout from '../../components/Layout/PageLayout';

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [formData, setFormData] = useState<ChangePasswordRequest>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false
  });

  const handleInputChange = (field: keyof ChangePasswordRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // 計算密碼強度
    if (field === 'newPassword') {
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const calculatePasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return Math.min(strength, 5);
  };

  const getPasswordStrengthColor = (strength: number) => {
    if (strength <= 2) return 'error';
    if (strength <= 3) return 'warning';
    return 'success';
  };

  const getPasswordStrengthText = (strength: number) => {
    if (strength <= 2) return '弱';
    if (strength <= 3) return '中等';
    if (strength <= 4) return '強';
    return '很強';
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // 驗證表單
    if (!formData.oldPassword || !formData.newPassword || !formData.confirmPassword) {
      showSnackbar('請填寫所有欄位', 'error');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      showSnackbar('新密碼和確認密碼不匹配', 'error');
      return;
    }

    if (formData.newPassword.length < 6) {
      showSnackbar('新密碼長度至少需要6個字符', 'error');
      return;
    }

    if (formData.oldPassword === formData.newPassword) {
      showSnackbar('新密碼不能與舊密碼相同', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.changeCurrentUserPassword(formData);

      if (response.success) {
        showSnackbar('密碼更改成功', 'success');
        setFormData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setPasswordStrength(0);
        setTimeout(() => {
          navigate('/settings');
        }, 1000);
      } else {
        showSnackbar(response.message || '密碼更改失敗', 'error');
      }
    } catch (error: any) {
      showSnackbar('密碼更改失敗: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const togglePasswordVisibility = (field: 'old' | 'new' | 'confirm') => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const passwordRequirements = [
    { text: '至少6個字符', met: formData.newPassword.length >= 6 },
    { text: '不能包含特殊字符', met: !/[^A-Za-z0-9]/.test(formData.newPassword) },
    { text: '不能與舊密碼相同', met: formData.oldPassword !== formData.newPassword },
    { text: '新密碼與確認密碼匹配', met: formData.newPassword === formData.confirmPassword && formData.confirmPassword !== '' }
  ];

  return (
    <PageLayout title="更改密碼">
      <Container maxWidth="md">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            更改密碼
          </Typography>
          <Typography variant="body1" color="text.secondary">
            為了您的帳戶安全，請定期更改密碼
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* 密碼更改表單 */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SecurityIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                <Typography variant="h5">
                  更改密碼
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="當前密碼"
                      type={showPassword.old ? 'text' : 'password'}
                      value={formData.oldPassword}
                      onChange={handleInputChange('oldPassword')}
                      required
                      disabled={loading}
                      helperText="請輸入您當前的密碼"
                      InputProps={{
                        endAdornment: (
                          <Button
                            size="small"
                            onClick={() => togglePasswordVisibility('old')}
                            disabled={loading}
                          >
                            {showPassword.old ? '隱藏' : '顯示'}
                          </Button>
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="新密碼"
                      type={showPassword.new ? 'text' : 'password'}
                      value={formData.newPassword}
                      onChange={handleInputChange('newPassword')}
                      required
                      disabled={loading}
                      helperText={`密碼強度: ${getPasswordStrengthText(passwordStrength)}`}
                      InputProps={{
                        endAdornment: (
                          <Button
                            size="small"
                            onClick={() => togglePasswordVisibility('new')}
                            disabled={loading}
                          >
                            {showPassword.new ? '隱藏' : '顯示'}
                          </Button>
                        )
                      }}
                    />

                    {/* 密碼強度指示器 */}
                    {formData.newPassword && (
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {[1, 2, 3, 4, 5].map((level) => (
                            <Box
                              key={level}
                              sx={{
                                height: 4,
                                flex: 1,
                                backgroundColor: level <= passwordStrength
                                  ? `${getPasswordStrengthColor(passwordStrength)}.main`
                                  : 'grey.300',
                                borderRadius: 1
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="確認新密碼"
                      type={showPassword.confirm ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleInputChange('confirmPassword')}
                      required
                      disabled={loading}
                      helperText="請再次輸入新密碼"
                      InputProps={{
                        endAdornment: (
                          <Button
                            size="small"
                            onClick={() => togglePasswordVisibility('confirm')}
                            disabled={loading}
                          >
                            {showPassword.confirm ? '隱藏' : '顯示'}
                          </Button>
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
                    >
                      {loading ? '更改中...' : '更改密碼'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* 密碼要求和提示 */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="primary" />
                密碼要求
              </Typography>

              <List dense>
                {passwordRequirements.map((req, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {req.met ? (
                        <CheckIcon color="success" fontSize="small" />
                      ) : (
                        <WarningIcon color="warning" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={req.text}
                      primaryTypographyProps={{
                        color: req.met ? 'success.main' : 'warning.main',
                        fontSize: '0.875rem'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* 安全提示 */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon color="primary" />
                安全提示
              </Typography>

              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="定期更改密碼"
                    secondary="建議每3-6個月更改一次密碼"
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="不要使用常見密碼"
                    secondary="避免使用生日、姓名等容易被猜到的密碼"
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="不要在公共場所輸入密碼"
                    secondary="確保在安全的環境中更改密碼"
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        {/* 提示訊息 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </PageLayout>
  );
};

export default ChangePasswordPage;
